import React, { useState, useEffect } from 'react';
import { Shield, CheckCircle, Star, ChevronDown, ChevronUp, Mail, User, Phone, MessageSquare, Award, Users, Target, Zap, Globe, TrendingUp, ArrowRight, FileCheck, Lock, Scale, ChevronLeft, ChevronRight, HelpCircle, Sparkles } from 'lucide-react';
import { testimonials } from '../mockData';
import { toast } from 'sonner';
import PricingSection from '../components/PricingSection';
import UniversalPopupForm from '../components/UniversalPopupForm';
import SEO from '../components/SEO';
import { submitLead } from '../services/apiService';

const LandingPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+1',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  // Auto-slide testimonials (3 at a time)
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => {
        const next = prev + 3;
        return next >= testimonials.length ? 0 : next;
      });
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await submitLead(formData);
      
      if (result.success) {
        toast.success(result.message);
        // Redirect to application page
        window.location.href = `/application/main?customerid=${result.customerId}`;
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChatClick = () => {
    if (window.LiveChatWidget) {
      window.LiveChatWidget.call('maximize');
    } else {
      toast.info('Chat is loading... Please try again in a moment.');
    }
  };

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => {
      const next = prev + 3;
      return next >= testimonials.length ? 0 : next;
    });
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => {
      const next = prev - 3;
      return next < 0 ? Math.max(0, testimonials.length - 3) : next;
    });
  };

  const faqs = [
    {
      question: "How long does the trademark registration process take in Canada?",
      answer: "The complete CIPO trademark registration process typically takes 12-18 months from filing to final approval. However, we submit your application promptly to get the process started immediately. The timeline depends on CIPO's examination schedule and whether any office actions are required."
    },
    {
      question: "What's included in your trademark registration service?",
      answer: "Our service includes a comprehensive trademark search, professional application preparation, CIPO filing, classification selection, response to office actions, and ongoing status monitoring until your trademark is registered. We handle all communications with CIPO on your behalf."
    },
    {
      question: "Do I need a trademark if I already have a business name?",
      answer: "Yes! A business name registration only reserves your name in your province for business purposes. A trademark gives you exclusive nationwide rights to use your brand name, logo, or slogan in commerce and prevents others from using it across all of Canada."
    },
    {
      question: "What if my trademark application is rejected?",
      answer: "With our 98% success rate, rejections are rare. However, if your application faces objections, we respond to all office actions and work with CIPO examiners to resolve issues. Our expert team knows how to navigate CIPO requirements to maximize your approval chances."
    },
    {
      question: "Can I trademark a logo and business name together?",
      answer: "Yes! You can file a combined trademark application that protects both your logo and business name together, or file separate applications for each. We'll recommend the best strategy based on your specific branding needs and budget."
    },
    {
      question: "How much does trademark registration cost?",
      answer: "Our service fees vary by package, starting with basic filing services to comprehensive protection packages. Government CIPO fees are separate and set by the Canadian Intellectual Property Office. Contact us for detailed pricing tailored to your needs."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Protect Your Brand Before It's Too Late"
        description="Professional CIPO trademark registration in 24 hours. 98% success rate. Secure exclusive rights to your business name, logo, and brand identity across all of Canada."
        keywords="fast trademark registration Canada, CIPO filing, brand protection, register trademark online, Canadian business trademark"
      />
      
      {/* Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <img 
                src="/assets/img/brand/logo-dark.png"
                alt="Trademark Flare Logo"
                className="h-10 w-auto"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setIsPopupOpen(true)}
                className="hidden sm:inline-flex items-center px-6 py-2.5 bg-gradient-to-r from-brand-600 to-brand-800 text-white font-semibold rounded-lg hover:from-brand-700 hover:to-brand-900 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                Register My Trademark
              </button>
              <button
                onClick={handleChatClick}
                className="inline-flex items-center px-6 py-2.5 border-2 border-brand-600 text-brand-600 font-semibold rounded-lg hover:bg-brand-50 transition-all duration-200"
              >
                Chat Now
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Redesigned for Landing Page */}
      <section className="relative pt-24 pb-16 overflow-hidden">
        {/* Canadian Landscape Background */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1728330041542-b3c2b7e843bd"
            alt="Beautiful Canadian landscape"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-brand-600/90 via-brand-700/85 to-brand-800/90"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Side - Enhanced Content */}
            <div className="text-white">
              <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm rounded-full px-5 py-2.5 mb-8">
                <Sparkles className="w-4 h-4 text-brand-300" />
                <span className="text-white font-bold text-sm">#1 Choice for Canadian Businesses</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-black mb-4 leading-tight">
                Protect Your Brand
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-300 to-brand-400">
                  Before It's Too Late
                </span>
              </h1>
              
              <p className="text-lg md:text-xl text-brand-100 mb-6 leading-relaxed">
                Professional CIPO trademark registration. Secure exclusive rights to your business name, logo, and brand identity across all of Canada.
              </p>

              {/* Trust Indicators - Redesigned */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 border border-white/20">
                  <div className="flex items-center space-x-2 mb-1">
                    <Award className="w-5 h-5 text-brand-300" />
                    <div className="text-2xl font-black">98%</div>
                  </div>
                  <div className="text-xs text-brand-200">Success Rate</div>
                </div>
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 border border-white/20">
                  <div className="flex items-center space-x-2 mb-1">
                    <Users className="w-5 h-5 text-brand-300" />
                    <div className="text-2xl font-black">500+</div>
                  </div>
                  <div className="text-xs text-brand-200">Brands Protected</div>
                </div>
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 border border-white/20">
                  <div className="flex items-center space-x-2 mb-1">
                    <Zap className="w-5 h-5 text-brand-300" />
                    <div className="text-2xl font-black">24h</div>
                  </div>
                  <div className="text-xs text-brand-200">Fast Filing</div>
                </div>
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 border border-white/20">
                  <div className="flex items-center space-x-2 mb-1">
                    <Shield className="w-5 h-5 text-brand-300" />
                    <div className="text-2xl font-black">100%</div>
                  </div>
                  <div className="text-xs text-brand-200">CIPO Certified</div>
                </div>
              </div>
            </div>

            {/* Right Side - Redesigned Form */}
            <div className="relative">
              {/* Decorative Elements */}
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-400 to-brand-500 rounded-3xl opacity-20 blur-2xl"></div>
              
              <div className="relative bg-white rounded-3xl shadow-2xl p-6 border-4 border-brand-400/30">
                {/* Form Header - CTA Oriented */}
                <div className="text-center mb-4">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-brand-600 to-brand-600 rounded-2xl mb-3 shadow-lg">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-xl font-black text-gray-900 mb-1">
                    Claim Your Trademark Now
                  </h2>
                  <p className="text-gray-600 text-xs font-semibold">
                    Don't Let Competitors Steal Your Brand Name!
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-3">
                  {/* Name */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full pl-10 pr-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:ring-4 focus:ring-brand-100 transition-all outline-none text-gray-900 font-medium text-sm"
                        placeholder="John Smith"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full pl-10 pr-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:ring-4 focus:ring-brand-100 transition-all outline-none text-gray-900 font-medium text-sm"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={formData.countryCode}
                        onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                        className="px-2 py-2.5 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:ring-4 focus:ring-brand-100 transition-all outline-none text-gray-900 font-bold text-xs"
                      >
                        <option value="+1">+1 Canada</option>
                        <option value="+1">🇺🇸 +1 USA</option>
                        <option value="+44">🇬🇧 +44 UK</option>
                        <option value="+91">🇮🇳 +91 India</option>
                        <option value="+86">🇨🇳 +86 China</option>
                        <option value="+81">🇯🇵 +81 Japan</option>
                        <option value="+82">🇰🇷 +82 S. Korea</option>
                        <option value="+61">🇦🇺 +61 Australia</option>
                        <option value="+49">🇩🇪 +49 Germany</option>
                        <option value="+33">🇫🇷 +33 France</option>
                        <option value="+39">🇮🇹 +39 Italy</option>
                        <option value="+34">🇪🇸 +34 Spain</option>
                        <option value="+7">🇷🇺 +7 Russia</option>
                        <option value="+55">🇧🇷 +55 Brazil</option>
                        <option value="+52">🇲🇽 +52 Mexico</option>
                        <option value="+27">🇿🇦 +27 S. Africa</option>
                        <option value="+971">🇦🇪 +971 UAE</option>
                        <option value="+65">🇸🇬 +65 Singapore</option>
                        <option value="+31">🇳🇱 +31 Netherlands</option>
                        <option value="+46">🇸🇪 +46 Sweden</option>
                      </select>
                      <div className="relative flex-1">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            if (value.length <= 10) {
                              setFormData({ ...formData, phone: value });
                            }
                          }}
                          className="w-full pl-10 pr-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:ring-4 focus:ring-brand-100 transition-all outline-none text-gray-900 font-medium text-sm"
                          placeholder="4165551234"
                          maxLength="10"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      Your Message
                    </label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                      <textarea
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        rows="2"
                        className="w-full pl-10 pr-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:ring-4 focus:ring-brand-100 transition-all outline-none resize-none text-gray-900 text-sm"
                        placeholder="Tell us about your trademark..."
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-brand-600 via-brand-700 to-brand-800 text-white font-black py-3 px-6 rounded-xl hover:from-brand-700 hover:via-brand-800 hover:to-brand-900 transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 shadow-xl hover:shadow-2xl text-sm"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Submitting...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center space-x-2">
                        <Shield className="w-5 h-5" />
                        <span>Secure My Brand NOW →</span>
                      </div>
                    )}
                  </button>

                  {/* Trust Badge */}
                  <div className="text-center">
                    <div className="flex items-center justify-center space-x-2 text-xs text-gray-500 mb-1">
                      <CheckCircle className="w-3 h-3 text-green-500" />
                      <span>🔒 Free Consultation • No Credit Card</span>
                    </div>
                    <p className="text-xs text-brand-600 font-bold">
                      ⚡ Join 500+ Protected Brands
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Section 1 - Why Trademark Protection Matters */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-600 to-brand-800 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.pexels.com/photos/3183197/pexels-photo-3183197.jpeg"
                alt="Business strategy and brand protection"
                className="relative rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-6 max-w-xs">
                <div className="flex items-center space-x-3 mb-2">
                  <div className="bg-brand-100 rounded-full p-2">
                    <Shield className="w-5 h-5 text-brand-600" />
                  </div>
                  <span className="font-bold text-gray-900">Brand Protection</span>
                </div>
                <p className="text-sm text-gray-600">Secure your business identity nationwide</p>
              </div>
            </div>

            <div>
              <div className="inline-block bg-brand-100 text-brand-600 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
                Why It Matters
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Don't Risk Losing Your Brand to Copycats
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Without trademark protection, competitors can legally use your business name, logo, or slogan. Your years of hard work building brand recognition could vanish overnight.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="bg-red-100 rounded-lg p-3 flex-shrink-0">
                    <Lock className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Exclusive Rights</h3>
                    <p className="text-gray-600">Gain exclusive legal rights to use your brand name and logo across Canada.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-green-100 rounded-lg p-3 flex-shrink-0">
                    <Scale className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Legal Protection</h3>
                    <p className="text-gray-600">Take legal action against anyone who infringes on your trademark rights.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-brand-100 rounded-lg p-3 flex-shrink-0">
                    <TrendingUp className="w-6 h-6 text-brand-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Increase Brand Value</h3>
                    <p className="text-gray-600">Registered trademarks significantly increase your business's market value.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Section 2 - CIPO Expertise - Redesigned */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-green-100 text-green-600 rounded-full px-6 py-3 mb-6 text-sm font-bold">
              CIPO Expertise
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
              Navigate CIPO With
              <br />
              <span className="text-brand-600">Professional Guidance</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The Canadian Intellectual Property Office trademark process is complex. Let our experts handle everything while you focus on your business.
            </p>
          </div>

          {/* Stats Grid */}
          <div className="md:grid md:grid-cols-3 md:gap-8 flex md:flex-none overflow-x-auto snap-x snap-mandatory gap-4 pb-4 md:pb-0 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0 mb-16">
            <div className="bg-gradient-to-br from-red-50 to-white rounded-3xl p-6 md:p-8 border-2 border-red-100 text-center min-w-full md:min-w-0 snap-center flex-shrink-0">
              <div className="text-4xl md:text-5xl font-black text-red-600 mb-2 md:mb-3">40%</div>
              <div className="text-gray-700 font-bold mb-2 text-sm md:text-base">DIY Rejection Rate</div>
              <p className="text-xs md:text-sm text-gray-600">Most self-filed applications get rejected due to errors</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-white rounded-3xl p-6 md:p-8 border-2 border-green-100 text-center min-w-full md:min-w-0 snap-center flex-shrink-0">
              <div className="text-4xl md:text-5xl font-black text-green-600 mb-2 md:mb-3">98%</div>
              <div className="text-gray-700 font-bold mb-2 text-sm md:text-base">Our Success Rate</div>
              <p className="text-xs md:text-sm text-gray-600">Expert filing ensures maximum approval chances</p>
            </div>
            <div className="bg-gradient-to-br from-brand-50 to-white rounded-3xl p-6 md:p-8 border-2 border-brand-100 text-center min-w-full md:min-w-0 snap-center flex-shrink-0">
              <div className="text-4xl md:text-5xl font-black text-brand-600 mb-2 md:mb-3">100%</div>
              <div className="text-gray-700 font-bold mb-2 text-sm md:text-base">CIPO Compliance</div>
              <p className="text-xs md:text-sm text-gray-600">Full adherence to all federal requirements</p>
            </div>
          </div>

          {/* Features Grid */}
          <div className="md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-6 flex md:flex-none overflow-x-auto snap-x snap-mandatory gap-4 pb-4 md:pb-0 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0">
            <div className="bg-white rounded-2xl p-5 md:p-6 shadow-lg border-2 border-brand-100 hover:border-brand-600 transition-all min-w-full md:min-w-0 snap-center flex-shrink-0">
              <div className="bg-brand-100 rounded-xl p-3 w-fit mb-3 md:mb-4">
                <FileCheck className="w-6 h-6 md:w-8 md:h-8 text-brand-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2 text-base md:text-lg">Expert Classification</h3>
              <p className="text-gray-600 text-xs md:text-sm">Proper class selection for comprehensive protection</p>
            </div>

            <div className="bg-white rounded-2xl p-5 md:p-6 shadow-lg border-2 border-green-100 hover:border-green-600 transition-all min-w-full md:min-w-0 snap-center flex-shrink-0">
              <div className="bg-green-100 rounded-xl p-3 w-fit mb-3 md:mb-4">
                <Target className="w-6 h-6 md:w-8 md:h-8 text-green-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2 text-base md:text-lg">Thorough Search</h3>
              <p className="text-gray-600 text-xs md:text-sm">Complete CIPO database search to avoid conflicts</p>
            </div>

            <div className="bg-white rounded-2xl p-5 md:p-6 shadow-lg border-2 border-brand-100 hover:border-brand-600 transition-all min-w-full md:min-w-0 snap-center flex-shrink-0">
              <div className="bg-brand-100 rounded-xl p-3 w-fit mb-3 md:mb-4">
                <Users className="w-6 h-6 md:w-8 md:h-8 text-brand-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2 text-base md:text-lg">Dedicated Manager</h3>
              <p className="text-gray-600 text-xs md:text-sm">Your personal case manager throughout</p>
            </div>

            <div className="bg-white rounded-2xl p-5 md:p-6 shadow-lg border-2 border-brand-100 hover:border-brand-600 transition-all min-w-full md:min-w-0 snap-center flex-shrink-0">
              <div className="bg-brand-100 rounded-xl p-3 w-fit mb-3 md:mb-4">
                <MessageSquare className="w-6 h-6 md:w-8 md:h-8 text-brand-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2 text-base md:text-lg">Office Actions</h3>
              <p className="text-gray-600 text-xs md:text-sm">Professional response to examiner queries</p>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
            <button
              onClick={() => setIsPopupOpen(true)}
              className="inline-flex items-center justify-center bg-gradient-to-r from-brand-600 to-brand-800 text-white font-bold px-8 py-4 rounded-lg hover:from-brand-700 hover:to-brand-900 transform hover:scale-105 transition-all duration-200 shadow-lg"
            >
              Register My Trademark
            </button>
            <button
              onClick={handleChatClick}
              className="inline-flex items-center justify-center bg-white text-brand-600 font-semibold px-8 py-4 rounded-lg border-2 border-brand-600 hover:bg-brand-50 transition-all duration-200"
            >
              <MessageSquare className="w-5 h-5 mr-2" />
              Chat Now
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <PricingSection />

      {/* Testimonials - Sliding Carousel */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Join hundreds of satisfied Canadian businesses
            </p>
          </div>

          <div className="relative">
            {/* Mobile View - Single Testimonial with Navigation */}
            <div className="md:hidden">
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <div className="flex mb-3">
                  {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                    <svg
                      key={i}
                      className="w-5 h-5 text-brand-400 fill-current"
                      viewBox="0 0 20 20"
                    >
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>
                <p className="text-base text-gray-700 italic mb-4 leading-relaxed">
                  "{testimonials[currentTestimonial].text}"
                </p>
                <div>
                  <p className="font-bold text-gray-900">
                    {testimonials[currentTestimonial].name}
                  </p>
                  <p className="text-brand-600 text-sm font-medium">
                    {testimonials[currentTestimonial].business}
                  </p>
                  <p className="text-xs text-gray-500">
                    {testimonials[currentTestimonial].location}
                  </p>
                </div>
              </div>
            </div>

            {/* Desktop Grid - 3 Testimonials at a Time */}
            <div className="hidden md:block">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {testimonials.slice(currentTestimonial, currentTestimonial + 3).map((testimonial, index) => (
                  <div key={index} className="bg-gradient-to-br from-brand-50 to-white rounded-3xl p-8 border-2 border-brand-100 hover:border-brand-600 transition-all duration-300 hover:shadow-xl">
                    <div className="flex items-center mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 text-brand-400 fill-brand-400" />
                      ))}
                    </div>
                    <p className="text-gray-700 mb-6 leading-relaxed">
                      "{testimonial.text}"
                    </p>
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-brand-600 to-brand-800 rounded-full flex items-center justify-center text-white font-bold">
                        {testimonial.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">{testimonial.name}</div>
                        <div className="text-sm text-gray-500">{testimonial.location}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Dots Indicator - Desktop Only */}
              <div className="flex justify-center mt-8 space-x-2">
                {Array.from({ length: Math.ceil(testimonials.length / 3) }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonial(index * 3)}
                    className={`transition-all duration-300 rounded-full ${
                      Math.floor(currentTestimonial / 3) === index ? 'bg-brand-600 w-8 h-3' : 'bg-gray-300 w-3 h-3'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Navigation Buttons - Both Mobile and Desktop */}
            {currentTestimonial > 0 && (
              <button
                onClick={prevTestimonial}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 bg-white hover:bg-gray-50 rounded-full p-3 shadow-lg transition-all duration-200 hover:scale-110 z-10 border-2 border-brand-100"
              >
                <ChevronLeft className="w-6 h-6 text-brand-600" />
              </button>
            )}
            {(currentTestimonial + 1 < testimonials.length && window.innerWidth < 768) || (currentTestimonial + 3 < testimonials.length && window.innerWidth >= 768) ? (
              <button
                onClick={nextTestimonial}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 bg-white hover:bg-gray-50 rounded-full p-3 shadow-lg transition-all duration-200 hover:scale-110 z-10 border-2 border-brand-100"
              >
                <ChevronRight className="w-6 h-6 text-brand-600" />
              </button>
            ) : null}
          </div>
        </div>
      </section>

      {/* FAQ Section - Service Page Style */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Common questions about Canadian trademark registration
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left - Image */}
            <div className="relative lg:sticky lg:top-8">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-600 to-brand-600 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.pexels.com/photos/8171203/pexels-photo-8171203.jpeg"
                alt="Professional consultation"
                className="relative rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-xl shadow-xl p-6">
                <h3 className="font-bold text-gray-900 mb-2 text-lg">Still Have Questions?</h3>
                <p className="text-gray-600 text-sm mb-4">Our trademark experts are here to help answer all your CIPO registration questions.</p>
                <button
                  onClick={() => setIsPopupOpen(true)}
                  className="w-full bg-gradient-to-r from-brand-600 to-brand-800 text-white font-semibold py-2 px-4 rounded-lg hover:from-brand-700 hover:to-brand-900 transition-all text-sm"
                >
                  Contact Us
                </button>
              </div>
            </div>

            {/* Right - FAQs */}
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors duration-200"
                  >
                    <div className="flex items-start space-x-4 flex-1">
                      <HelpCircle className="w-6 h-6 text-brand-600 flex-shrink-0 mt-1" />
                      <span className="font-semibold text-gray-900 text-lg pr-4">{faq.question}</span>
                    </div>
                    {openFaq === index ? (
                      <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-6">
                      <div className="pl-10 text-gray-600 leading-relaxed">
                        {faq.answer}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Creative Final CTA */}
      <section className="relative py-32 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1570902128092-950ebe50a3da"
            alt="Halifax waterfront cityscape"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-900/95 via-brand-800/90 to-brand-700/95"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-brand-900/50 to-transparent"></div>
        </div>

        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-white/10 rounded-full blur-2xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-brand-500/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-brand-500/10 rounded-full blur-2xl animate-pulse" style={{animationDelay: '2s'}}></div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Animated Badge */}
          <div className="inline-block animate-bounce mb-8">
            <div className="bg-white/20 backdrop-blur-md rounded-full px-6 py-3 border-2 border-white/30">
              <span className="text-white font-bold text-sm">🍁 Trusted by 500+ Canadian Businesses</span>
            </div>
          </div>

          {/* Main Heading */}
          <h2 className="text-5xl md:text-6xl font-black text-white mb-6 leading-tight">
            Don't Let Someone Else
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-300 via-brand-400 to-brand-500">
              Steal Your Brand Name
            </span>
          </h2>

          <p className="text-xl md:text-2xl text-brand-100 mb-12 max-w-3xl mx-auto leading-relaxed">
            Every day you wait is another opportunity for competitors to register your brand. Protect your business name with CIPO registration today.
          </p>

          {/* Stats Row */}
          <div className="flex flex-wrap justify-center gap-6 mb-12 max-w-3xl mx-auto">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 text-center">
              <div className="text-4xl font-black text-white mb-2">98%</div>
              <div className="text-brand-200 text-sm">Approval Rate</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 text-center">
              <div className="text-4xl font-black text-white mb-2">500+</div>
              <div className="text-brand-200 text-sm">Brands Protected</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 text-center">
              <div className="text-4xl font-black text-white mb-2">24h</div>
              <div className="text-brand-200 text-sm">Fast Filing</div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <button
              onClick={() => setIsPopupOpen(true)}
              className="group inline-flex items-center space-x-3 bg-white text-brand-600 font-black px-10 py-5 rounded-full hover:shadow-2xl transform hover:scale-105 transition-all duration-200 text-lg"
            >
              <Shield className="w-6 h-6" />
              <span>Protect Your Brand</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={handleChatClick}
              className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-md text-white font-bold px-10 py-5 rounded-full border-2 border-white/30 hover:bg-white/20 transition-all duration-200 text-lg"
            >
              <MessageSquare className="w-6 h-6" />
              <span>Chat with Expert</span>
            </button>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-wrap items-center justify-center gap-8 text-white/80">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-300" />
              <span className="text-sm font-medium">Free Consultation</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-300" />
              <span className="text-sm font-medium">No Hidden Fees</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-300" />
              <span className="text-sm font-medium">CIPO Certified</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-300" />
              <span className="text-sm font-medium">Expert Support</span>
            </div>
          </div>
        </div>
      </section>

      {/* Universal Popup Form */}
      <UniversalPopupForm 
        isOpen={isPopupOpen} 
        onClose={() => setIsPopupOpen(false)}
        title="Protect Your Brand Today"
      />

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              © 2026 Trademark Flare. All rights reserved.
            </p>
            <div className="flex items-center space-x-6 text-sm">
              <a href="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">
                Privacy Policy
              </a>
              <span className="text-gray-600">|</span>
              <a href="/terms-of-service" className="text-gray-400 hover:text-white transition-colors">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
